import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Container, Row, Col } from "react-bootstrap";
import { toast } from "react-toastify";
import styled from "styled-components";
import { MdEmail, MdDelete } from "react-icons/md";
import useAxios from "../../customHooks/useAxios";
import RenderOnRole from "../../auth/RenderOnRole";
import ImsSystemRole from "../../auth/roles/ImsSystemRole";
import ToastAlertView from "../common/alert/ToastAlertView";
import AddEmailReceiverModal from "./modals/AddEmailReceiverModal";
import ConfirmModal from "../common/confirm/ConfirmModal";
import BackwardButton from "../common/layoutSub/BackwardButton";
import PageTitleWithDepth from "../common/layoutSub/PageTitleWithDepth";
import "./EmailReceiverManagementStyle.css";

// Styled Components
const SectionTitle = styled.h3`
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin-bottom: 15px;
    padding-bottom: 10px;
    border-bottom: 2px solid #fc7242;
`;

const AddButton = styled.button`
    width: 100%;
    padding: 10px;
    background-color: #fc7242;
    color: white;
    border: none;
    border-radius: 5px;
    font-size: 14px;
    cursor: pointer;
    transition: background-color 0.3s;
    margin-bottom: 15px;

    &:hover {
        background-color: #e55a2b;
    }

    &:disabled {
        background-color: #c2c2c2;
        cursor: not-allowed;
    }
`;

const EmailItem = styled.div`
    width: 100%;
    padding: 12px 15px;
    margin-bottom: 10px;
    border: 1px solid #ebebeb;
    border-radius: 5px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    background-color: white;
    transition: all 0.2s;
    cursor: default;

    &:hover {
        border-color: #fc7242;
        background-color: #fff8f6;
    }
`;

const EmailText = styled.div`
    flex: 1;
    display: flex;
    align-items: center;
    color: #333;
    font-size: 14px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const DeleteButton = styled.button`
    background-color: transparent;
    border: none;
    color: #dc3545;
    cursor: pointer;
    padding: 5px;
    display: flex;
    align-items: center;
    transition: color 0.2s;

    &:hover {
        color: #c82333;
    }
`;

const ListContainer = styled.div`
    width: 100%;
    height: 600px;
    border: 1px solid #ebebeb;
    border-radius: 5px;
    padding: 15px;
    overflow-y: auto;
    background-color: #fafafa;
`;

const EmptyMessage = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #999;
    font-size: 14px;
`;

const EmailReceiverManagement = ({ isTabContent = false }) => {
    const { isAxiosLoading, axiosCall } = useAxios();
    const { t } = useTranslation();

    // List data state
    const [orderEmailList, setOrderEmailList] = useState([]);
    const [developmentEmailList, setDevelopmentEmailList] = useState([]);

    // Modal state
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [addModalEmailType, setAddModalEmailType] = useState("ORDER");
    const [isDeleteConfirm, setIsDeleteConfirm] = useState(false);
    const [selectedReceiver, setSelectedReceiver] = useState(null);

    // Fetch email receiver lists
    const fetchEmailReceiverLists = async () => {
        try {
            // Fetch ORDER emails
            const orderRes = await axiosCall(
                "GET",
                "/emailReceiver",
                [],
                {},
                null,
                { email_type: "ORDER" },
                "default"
            );

            if (orderRes.data.error_code === "NO_ERROR") {
                setOrderEmailList(orderRes.data.item.emailReceiverList || []);
            }

            // Fetch DEVELOPMENT emails
            const devRes = await axiosCall(
                "GET",
                "/emailReceiver",
                [],
                {},
                null,
                { email_type: "DEVELOPMENT" },
                "default"
            );

            if (devRes.data.error_code === "NO_ERROR") {
                setDevelopmentEmailList(devRes.data.item.emailReceiverList || []);
            }
        } catch (err) {
            console.log(err);
            toast.error(<ToastAlertView message={t("message.failed")} />);
        }
    };

    // Delete email receiver
    const handleDelete = async () => {
        if (!selectedReceiver) return;

        try {
            const res = await axiosCall(
                "DELETE",
                "/emailReceiver/:1",
                [selectedReceiver.emailReceiverId],
                {},
                null,
                {},
                "default"
            );

            if (res.data.error_code === "NO_ERROR") {
                toast.success(<ToastAlertView message={t("message.deleted")} />);
                setIsDeleteConfirm(false);
                setSelectedReceiver(null);
                fetchEmailReceiverLists();
            }
        } catch (err) {
            console.log(err);
            toast.error(<ToastAlertView message={t("message.failed")} />);
        }
    };

    const handleDeleteClick = (receiver) => {
        setSelectedReceiver(receiver);
        setIsDeleteConfirm(true);
    };

    const handleAddClick = (emailType) => {
        setAddModalEmailType(emailType);
        setIsAddModalOpen(true);
    };

    // Render email list items
    const renderEmailList = (emailList) => {
        if (emailList.length === 0) {
            return <EmptyMessage>{t("emailReceiver.noEmails")}</EmptyMessage>;
        }

        return emailList.map((receiver, idx) => (
            <EmailItem key={idx}>
                <EmailText>
                    <MdEmail style={{ marginRight: "10px", color: "#fc7242" }} size={20} />
                    {receiver.emailAddress}
                </EmailText>
                <RenderOnRole
                    type={""}
                    roleName={""}
                    allowedRing={0}
                    nothingOnRejected={true}
                    isForSuperMaster
                >
                    <DeleteButton onClick={() => handleDeleteClick(receiver)}>
                        <MdDelete size={20} />
                    </DeleteButton>
                </RenderOnRole>
            </EmailItem>
        ));
    };

    // Effects
    useEffect(() => {
        fetchEmailReceiverLists();
    }, []);

    const content = (
        <>
            <Container fluid style={{ padding: isTabContent ? "0" : "20px" }}>
                {/* Two Column Layout */}
                <Row style={{ marginTop: isTabContent ? "0" : "20px" }}>
                    {/* ORDER Emails */}
                    <Col md={6}>
                        <SectionTitle>{t("emailReceiver.orderEmails")}</SectionTitle>
                        <RenderOnRole
                            type={"POST"}
                            roleName={""}
                            allowedRing={0}
                            nothingOnRejected={true}
                            isForSuperMaster
                        >
                            <AddButton onClick={() => handleAddClick("ORDER")}>
                                {t("emailReceiver.addOrderEmail")}
                            </AddButton>
                        </RenderOnRole>
                        <ListContainer>
                            {renderEmailList(orderEmailList)}
                        </ListContainer>
                    </Col>

                    {/* DEVELOPMENT Emails */}
                    <Col md={6}>
                        <SectionTitle>{t("emailReceiver.developmentEmails")}</SectionTitle>
                        <RenderOnRole
                            type={"POST"}
                            roleName={""}
                            allowedRing={0}
                            nothingOnRejected={true}
                            isForSuperMaster
                        >
                            <AddButton onClick={() => handleAddClick("DEVELOPMENT")}>
                                {t("emailReceiver.addDevelopmentEmail")}
                            </AddButton>
                        </RenderOnRole>
                        <ListContainer>
                            {renderEmailList(developmentEmailList)}
                        </ListContainer>
                    </Col>
                </Row>

                {/* Modals */}
                <AddEmailReceiverModal
                    isOpen={isAddModalOpen}
                    setIsOpen={setIsAddModalOpen}
                    emailType={addModalEmailType}
                    onRefresh={fetchEmailReceiverLists}
                />

                <ConfirmModal
                    isOpen={isDeleteConfirm}
                    setIsOpen={setIsDeleteConfirm}
                    title={t("emailReceiver.deleteConfirm")}
                    confirmLabel={t("button.yes")}
                    rejectLabel={t("button.no")}
                    onConfirm={handleDelete}
                    onReject={() => {
                        setIsDeleteConfirm(false);
                        setSelectedReceiver(null);
                    }}
                />
            </Container>
        </>
    );

    if (isTabContent) {
        return content;
    }

    return (
        <RenderOnRole
            type={""}
            roleName={""}
            allowedRing={0}
            isForSuperMaster
        >
            {/* Title */}
            <div className={"headingTitle"}>
                <BackwardButton isShow={false} />
                <PageTitleWithDepth
                    titles={[t("emailReceiver.title")]}
                    depth={1}
                />
            </div>
            <div className={"contentContainer"}>
                {content}
            </div>
        </RenderOnRole>
    );
};

export default EmailReceiverManagement;
